import {
  Alert,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Tag,
  Typography,
  theme,
} from "antd";
import {
  CheckCircleOutlined,
  ContainerOutlined,
  FileTextOutlined,
  ShopOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { getMissingFieldLabel } from "../utils/fieldLabels";
import { useTransportTypes } from "../../../hooks/useTransportTypes";
import { useIncoterms } from "../../../hooks/useIncoterms";
import type { CommercialInvoiceAnalysisResult } from "../../../features/commercialInvoiceAnalysis/types";
import { coerceToString } from "../utils/mapAnalysisToBooking";

const { Title, Text } = Typography;

interface BookingDraftPreviewProps {
  result: CommercialInvoiceAnalysisResult;
  missingFields: string[];
  fileName?: string | null;
}

const formatUsd = (value: number | null | undefined): string | null => {
  if (value == null || !Number.isFinite(value)) return null;
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const BookingDraftPreview = ({
  result,
  missingFields,
  fileName,
}: BookingDraftPreviewProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { state } = useBookingWizard();
  const { transportTypes } = useTransportTypes();
  const { incoterms } = useIncoterms();

  const extracted = result.deepCheck?.extracted;
  const products = state.products ?? [];
  const transportName = transportTypes.find((item) => item.id === state.transport_type)?.name;
  const incotermName = incoterms.find((item) => item.id === state.booking_incoterm)?.name
    ?? coerceToString(extracted?.incoterm);

  const supplierName = coerceToString(extracted?.sellerName);
  const supplierLocation = [
    coerceToString(extracted?.sellerAddress)?.split(/[,;\n]/)[0],
    extracted?.countryOfOrigin
      ? coerceToString(extracted.countryOfOrigin)
      : null,
  ]
    .filter(Boolean)
    .slice(0, 2)
    .join(" · ");

  const totalValue = products.reduce((sum, product) => sum + (product.value ?? 0), 0);
  const totalPieces = products.reduce((sum, product) => sum + (product.pieces ?? 0), 0);

  return (
    <div style={{ width: "100%", maxWidth: 640, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <CheckCircleOutlined
          style={{ fontSize: 40, color: token.colorSuccess, marginBottom: 12 }}
        />
        <Title level={3} style={{ margin: 0 }}>
          {t("bookingWizardV2.draftPreview.title")}
        </Title>
        <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
          {t("bookingWizardV2.draftPreview.subtitle")}
        </Text>
        <Space wrap style={{ justifyContent: "center", marginTop: 12 }}>
          <Tag color="blue">{t("status.concept")}</Tag>
          {state.id && (
            <Tag icon={<FileTextOutlined />}>
              {t("bookingWizardV2.draftPreview.bookingId", { id: state.id })}
            </Tag>
          )}
          {state.reference && (
            <Tag>{t("bookingWizard.basicDetails.reference")}: {state.reference}</Tag>
          )}
          {fileName && <Tag>{fileName}</Tag>}
        </Space>
      </div>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <Card
            size="small"
            title={
              <Space>
                <ContainerOutlined />
                {t("bookingWizard.steps.transportDetails")}
              </Space>
            }
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t("bookingWizard.transportDetails.transportType")}>
                {transportName ?? (
                  <Text type="warning">{t("bookingWizardV2.draftPreview.notSet")}</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t("bookingWizard.transportDetails.incoterm")}>
                {incotermName ?? (
                  <Text type="warning">{t("bookingWizardV2.draftPreview.notSet")}</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            size="small"
            title={
              <Space>
                <ShopOutlined />
                {t("bookingWizard.steps.supplierDetails")}
              </Space>
            }
          >
            {supplierName ? (
              <>
                <Text strong style={{ display: "block" }}>
                  {supplierName}
                </Text>
                {supplierLocation && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {supplierLocation}
                  </Text>
                )}
              </>
            ) : (
              <Text type="warning">{t("bookingWizardV2.draftPreview.notSet")}</Text>
            )}
          </Card>
        </Col>

        <Col span={24}>
          <Card
            size="small"
            title={
              <Space>
                <ShoppingOutlined />
                {t("bookingWizard.steps.productDetails")}
                {products.length > 0 && (
                  <Tag color="processing">{products.length}</Tag>
                )}
              </Space>
            }
          >
            {products.length > 0 ? (
              <Space direction="vertical" style={{ width: "100%" }} size={8}>
                {products.map((product, index) => (
                  <div
                    key={`${product.name}-${index}`}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "8px 0",
                      borderBottom:
                        index < products.length - 1
                          ? `1px solid ${token.colorBorderSecondary}`
                          : undefined,
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Text strong style={{ display: "block" }}>
                        {product.name}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {[
                          product.pieces
                            ? t("bookingWizardV2.draftPreview.pieces", {
                                count: product.pieces,
                              })
                            : null,
                          product.hs_code ? `HS ${product.hs_code}` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Text>
                    </div>
                    <Text strong>{formatUsd(product.value) ?? "—"}</Text>
                  </div>
                ))}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingTop: 4,
                  }}
                >
                  <Text type="secondary">{t("bookingWizardV2.draftPreview.totals")}</Text>
                  <Space size={16}>
                    {totalPieces > 0 && (
                      <Text>
                        {t("bookingWizardV2.draftPreview.pieces", { count: totalPieces })}
                      </Text>
                    )}
                    {totalValue > 0 && <Text strong>{formatUsd(totalValue)}</Text>}
                  </Space>
                </div>
              </Space>
            ) : (
              <Text type="warning">{t("bookingWizardV2.draftPreview.noProducts")}</Text>
            )}
          </Card>
        </Col>
      </Row>

      {missingFields.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message={t("bookingWizardV2.draftPreview.reviewNeeded", {
            count: missingFields.length,
          })}
          description={
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {missingFields.slice(0, 5).map((field) => (
                <li key={field}>
                  <Text style={{ fontSize: 13 }}>
                    {getMissingFieldLabel(field, t)}
                  </Text>
                </li>
              ))}
              {missingFields.length > 5 && (
                <li>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {t("bookingWizardV2.draftPreview.andMore", {
                      count: missingFields.length - 5,
                    })}
                  </Text>
                </li>
              )}
            </ul>
          }
        />
      )}
    </div>
  );
};
